import { useState, useEffect } from 'react';

export default function HomePage() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [contactStatus, setContactStatus] = useState('');
  const [applyStatus, setApplyStatus] = useState('');
  const [activeSection, setActiveSection] = useState('home');

  useEffect(() => {
    const siteUrl = (import.meta.env.VITE_SITE_URL as string) || 'https://www.rulerlogistics.com';
    const schemas = [
      {
        '@context': 'https://schema.org',
        '@type': 'LocalBusiness',
        name: 'Ruler Logistic LLC',
        image: 'https://public.readdy.ai/ai/img_res/9444abc7-4567-436f-8d58-59ae5de20165.png',
        url: siteUrl,
        telephone: '+16302024180',
        email: 'rulerlogistics@gmail.com',
        address: {
          '@type': 'PostalAddress',
          streetAddress: '',
          addressLocality: 'Chicago',
          addressRegion: 'IL',
          postalCode: '60601',
          addressCountry: 'US',
        },
        geo: {
          '@type': 'GeoCoordinates',
          latitude: 41.8336,
          longitude: -87.9073,
        },
        description:
          'Chicago-based owner-operated trucking company offering freight hauling, long-distance runs, and local delivery across Illinois and the Midwest.',
        priceRange: '$$',
        openingHoursSpecification: [
          {
            '@type': 'OpeningHoursSpecification',
            dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
            opens: '07:00',
            closes: '20:00',
          },
          {
            '@type': 'OpeningHoursSpecification',
            dayOfWeek: ['Saturday'],
            opens: '08:00',
            closes: '16:00',
          },
        ],
        hasOfferCatalog: {
          '@type': 'OfferCatalog',
          name: 'Trucking Services',
          itemListElement: [
            {
              '@type': 'Offer',
              itemOffered: {
                '@type': 'Service',
                name: 'Freight Hauling',
                description:
                  'Full truckload (FTL) and partial loads across the Midwest and beyond. We handle dry van freight with care.',
              },
            },
            {
              '@type': 'Offer',
              itemOffered: {
                '@type': 'Service',
                name: 'Long-Distance Runs',
                description:
                  'Interstate routes from Chicago to destinations across the US. Reliable, timely, and professional.',
              },
            },
            {
              '@type': 'Offer',
              itemOffered: {
                '@type': 'Service',
                name: 'Local & Regional Delivery',
                description:
                  'Chicago metro area pickups and deliveries. Fast turnaround for local businesses and warehouses.',
              },
            },
          ],
        },
      },
      {
        '@context': 'https://schema.org',
        '@type': 'WebSite',
        name: 'Ruler Logistic LLC',
        url: siteUrl,
        description:
          'Ruler Logistic LLC – Chicago trucking company specializing in freight hauling, long-distance runs, and local delivery across Illinois and the Midwest.',
        publisher: {
          '@type': 'Organization',
          name: 'Ruler Logistic LLC',
          logo: {
            '@type': 'ImageObject',
            url: 'https://public.readdy.ai/ai/img_res/9444abc7-4567-436f-8d58-59ae5de20165.png',
          },
        },
      },
      {
        '@context': 'https://schema.org',
        '@type': 'WebPage',
        name: 'Ruler Logistics | Chicago Trucking Company',
        url: siteUrl,
        description:
          'Chicago-based owner-operated trucking. Freight hauling, long-distance runs, local delivery. CDL licensed & fully insured.',
        breadcrumb: {
          '@type': 'BreadcrumbList',
          itemListElement: [
            {
              '@type': 'ListItem',
              position: 1,
              name: 'Home',
              item: siteUrl,
            },
          ],
        },
      },
    ];

    const insertedIds: string[] = [];
    schemas.forEach((schema, i) => {
      const id = `rl-schema-${i}`;
      if (!document.getElementById(id)) {
        const script = document.createElement('script');
        script.type = 'application/ld+json';
        script.id = id;
        script.textContent = JSON.stringify(schema);
        document.head.appendChild(script);
        insertedIds.push(id);
      }
    });

    return () => {
      insertedIds.forEach((id) => {
        document.getElementById(id)?.remove();
      });
    };
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 60);
      const sections = ['home', 'about', 'apply', 'contact'];
      for (const id of sections) {
        const el = document.getElementById(id);
        if (el) {
          const rect = el.getBoundingClientRect();
          if (rect.top <= 100 && rect.bottom >= 100) {
            setActiveSection(id);
            break;
          }
        }
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollTo = (id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
    setMenuOpen(false);
  };

  const handleContact = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const data = new FormData(form);
    try {
      const res = await fetch('https://readdy.ai/api/form/d6qpvvkddmmni7ck8kr0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(data as any).toString(),
      });
      if (res.ok) {
        setContactStatus('success');
        form.reset();
        setTimeout(() => setContactStatus(''), 4000);
      } else {
        setContactStatus('error');
      }
    } catch {
      setContactStatus('error');
    }
  };

  const handleApply = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const data = new FormData(form);
    const params = new URLSearchParams();
    data.forEach((value, key) => {
      params.append(key, value as string);
    });
    try {
      const res = await fetch('https://readdy.ai/api/form/d72mk44ij602egd95la0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });
      if (res.ok) {
        setApplyStatus('success');
        form.reset();
        setTimeout(() => setApplyStatus(''), 4000);
      } else {
        setApplyStatus('error');
      }
    } catch {
      setApplyStatus('error');
    }
  };

  const navLinks = [
    { id: 'home', label: 'Home' },
    { id: 'about', label: 'About' },
    { id: 'apply', label: 'Drive With Us' },
    { id: 'contact', label: 'Contact' },
  ];

  return (
    <div className="min-h-screen bg-white font-sans">

      {/* NAV */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-white shadow-md' : 'bg-transparent'}`}>
        <div className="max-w-6xl mx-auto px-6 py-3 flex items-center justify-between">
          <button onClick={() => scrollTo('home')} className="cursor-pointer">
            <img
              src="https://public.readdy.ai/ai/img_res/9444abc7-4567-436f-8d58-59ae5de20165.png"
              alt="Ruler Logistics LLC"
              className="h-40 w-auto object-contain"
            />
          </button>

          {/* Desktop links */}
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map(link => (
              <button
                key={link.id}
                onClick={() => scrollTo(link.id)}
                className={`text-sm font-semibold tracking-wide transition-colors whitespace-nowrap cursor-pointer ${
                  activeSection === link.id
                    ? 'text-[#D4A017]'
                    : isScrolled ? 'text-gray-800 hover:text-[#D4A017]' : 'text-white hover:text-[#D4A017]'
                }`}
              >
                {link.label}
              </button>
            ))}
            <button
              onClick={() => scrollTo('contact')}
              className="bg-[#D4A017] text-white px-6 py-2 rounded-full text-sm font-bold hover:bg-[#b8880f] transition-colors whitespace-nowrap cursor-pointer"
            >
              Get a Quote
            </button>
          </div>

          {/* Mobile hamburger */}
          <button
            className={`md:hidden text-2xl cursor-pointer ${isScrolled ? 'text-gray-800' : 'text-white'}`}
            onClick={() => setMenuOpen(!menuOpen)}
          >
            <i className={menuOpen ? 'ri-close-line' : 'ri-menu-line'}></i>
          </button>
        </div>

        {/* Mobile menu */}
        {menuOpen && (
          <div className="md:hidden bg-white shadow-lg px-6 py-4 flex flex-col gap-4">
            {navLinks.map(link => (
              <button
                key={link.id}
                onClick={() => scrollTo(link.id)}
                className="text-left text-gray-800 font-semibold text-sm hover:text-[#D4A017] transition-colors cursor-pointer whitespace-nowrap"
              >
                {link.label}
              </button>
            ))}
          </div>
        )}
      </nav>

      {/* HERO */}
      <section id="home" className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <img
          src="https://storage.readdy-site.link/project_files/543841c4-c9e4-4ed0-8973-b395b0aadacc/e43f71ce-c520-4ba5-9999-60f1c53ba8c1_9C77C9D5-58B5-46A1-877C-E171EE5EA2A1.png?v=0e4a2b0053666def674e60aa205e11a1"
          alt="Ruler Logistics truck on the road"
          className="absolute inset-0 w-full h-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/55 via-black/35 to-black/60"></div>

        <div className="relative z-10 w-full max-w-5xl mx-auto px-6 text-center">
          <img
            src="https://public.readdy.ai/ai/img_res/9444abc7-4567-436f-8d58-59ae5de20165.png"
            alt="Ruler Logistics LLC Logo"
            className="h-[36rem] w-auto object-contain mx-auto mb-8 drop-shadow-2xl"
          />
          <h1 className="text-white font-extrabold text-5xl md:text-7xl leading-tight mb-4 tracking-tight drop-shadow-lg">
            Reliable Trucking<br />
            <span className="text-[#D4A017]">Across America</span>
          </h1>
          <p className="text-white/85 text-lg md:text-xl max-w-2xl mx-auto mb-10 leading-relaxed">
            Chicago-based owner-operator delivering freight safely and on time. Built on trust, driven by dedication.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={() => scrollTo('contact')}
              className="bg-[#D4A017] text-white px-10 py-4 rounded-full text-base font-bold hover:bg-[#b8880f] transition-all shadow-lg whitespace-nowrap cursor-pointer"
            >
              Request a Quote
            </button>
            <button
              onClick={() => scrollTo('apply')}
              className="bg-white/15 border-2 border-white text-white px-10 py-4 rounded-full text-base font-bold hover:bg-white/25 transition-all whitespace-nowrap cursor-pointer"
            >
              Drive With Us
            </button>
          </div>
        </div>

        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 animate-bounce">
          <i className="ri-arrow-down-line text-white/70 text-2xl"></i>
        </div>
      </section>

      {/* HIGHLIGHTS BAR */}
      <div className="bg-[#1a1a2e] py-8">
        <div className="max-w-5xl mx-auto px-6 grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
          {[
            { icon: 'ri-map-pin-2-line', label: 'Based in Chicago, IL' },
            { icon: 'ri-truck-line', label: 'Owner-Operated Fleet' },
            { icon: 'ri-shield-check-line', label: 'Fully Insured & Licensed' },
            { icon: 'ri-time-line', label: 'On-Time Delivery' },
          ].map((item, i) => (
            <div key={i} className="flex flex-col items-center gap-2">
              <div className="w-10 h-10 flex items-center justify-center">
                <i className={`${item.icon} text-[#D4A017] text-2xl`}></i>
              </div>
              <span className="text-white/80 text-sm font-medium">{item.label}</span>
            </div>
          ))}
        </div>
      </div>

      {/* ABOUT */}
      <section id="about" className="py-24 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="text-[#D4A017] text-sm font-bold uppercase tracking-widest">About Us</span>
              <h2 className="text-4xl md:text-5xl font-extrabold text-gray-900 mt-3 mb-6 leading-tight">
                Small Business,<br />Big Commitment
              </h2>
              <p className="text-gray-600 text-base leading-relaxed mb-5">
                Ruler Logistics LLC is a Chicago-based trucking company founded by a hands-on owner-operator with a passion for getting freight where it needs to go — safely, on time, and without hassle.
              </p>
              <p className="text-gray-600 text-base leading-relaxed mb-5">
                We run a tight, professional operation with our own trucks out of the Chicago area. Whether you need local hauls or long-distance freight across the Midwest and beyond, we treat every load like it's our own.
              </p>
              <p className="text-gray-600 text-base leading-relaxed mb-8">
                We believe in honest communication, fair rates, and showing up when we say we will. That's the Ruler Logistics way.
              </p>
              <div className="grid grid-cols-2 gap-6">
                {[
                  { value: '100%', label: 'Owner-Operated' },
                  { value: 'Chicago', label: 'Home Base, IL' },
                  { value: 'CDL', label: 'Licensed Drivers' },
                  { value: '24/7', label: 'Communication' },
                ].map((stat, i) => (
                  <div key={i} className="bg-gray-50 rounded-2xl p-5 border border-gray-100">
                    <div className="text-3xl font-extrabold text-[#D4A017] mb-1">{stat.value}</div>
                    <div className="text-gray-500 text-sm font-medium">{stat.label}</div>
                  </div>
                ))}
              </div>
            </div>

            <div className="relative">
              <div className="rounded-3xl overflow-hidden h-[480px] w-full shadow-2xl">
                <img
                  src="https://public.readdy.ai/ai/img_res/6a412f611d7cc3e1781091c8bb3f99ee.jpg"
                  alt="Ruler Logistics owner with truck"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="absolute -bottom-6 -left-6 bg-[#D4A017] rounded-2xl p-6 shadow-xl">
                <div className="text-white text-4xl font-extrabold">Chicago</div>
                <div className="text-white/80 text-sm mt-1">Illinois, USA</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SERVICES STRIP */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-14">
            <span className="text-[#D4A017] text-sm font-bold uppercase tracking-widest">What We Do</span>
            <h2 className="text-4xl font-extrabold text-gray-900 mt-3">Our Services</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                icon: 'ri-truck-line',
                title: 'Freight Hauling',
                desc: 'Full truckload (FTL) and partial loads across the Midwest and beyond. We handle dry van freight with care.',
              },
              {
                icon: 'ri-map-2-line',
                title: 'Long-Distance Runs',
                desc: 'Interstate routes from Chicago to destinations across the US. Reliable, timely, and professional.',
              },
              {
                icon: 'ri-building-2-line',
                title: 'Local & Regional',
                desc: 'Chicago metro area pickups and deliveries. Fast turnaround for local businesses and warehouses.',
              },
            ].map((s, i) => (
              <div key={i} className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
                <div className="w-14 h-14 flex items-center justify-center bg-[#D4A017]/10 rounded-xl mb-5">
                  <i className={`${s.icon} text-[#D4A017] text-2xl`}></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{s.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* DRIVER APPLICATION */}
      <section id="apply" className="py-24 bg-[#1a1a2e] relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <img
            src="https://readdy.ai/api/search-image?query=semi%20trucks%20parked%20in%20a%20large%20truck%20stop%20at%20night%20with%20lights%20glowing%20dark%20moody%20atmosphere%20american%20trucking%20industry%20wide%20shot&width=1920&height=600&seq=apply-bg-003&orientation=landscape"
            alt=""
            className="w-full h-full object-cover object-top"
          />
        </div>
        <div className="relative z-10 max-w-4xl mx-auto px-6">
          <div className="text-center mb-12">
            <span className="text-[#D4A017] text-sm font-bold uppercase tracking-widest">Join the Team</span>
            <h2 className="text-4xl md:text-5xl font-extrabold text-white mt-3 mb-4">Drive With Ruler Logistics</h2>
            <p className="text-white/65 text-base max-w-xl mx-auto">
              We're looking for experienced, reliable CDL drivers based in or around Chicago. Fill out the form below and we'll be in touch.
            </p>
          </div>

          <div className="bg-white rounded-3xl p-8 md:p-10 shadow-2xl">
            <form data-readdy-form id="driver-application-form" onSubmit={handleApply} className="space-y-5">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">First Name *</label>
                  <input
                    type="text"
                    name="first_name"
                    required
                    placeholder="John"
                    className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm outline-none focus:border-[#D4A017] transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">Last Name *</label>
                  <input
                    type="text"
                    name="last_name"
                    required
                    placeholder="Smith"
                    className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm outline-none focus:border-[#D4A017] transition-colors"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">Phone Number *</label>
                  <input
                    type="tel"
                    name="phone"
                    required
                    placeholder="+1 (312) 000-0000"
                    className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm outline-none focus:border-[#D4A017] transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">Email Address *</label>
                  <input
                    type="email"
                    name="email"
                    required
                    placeholder="john@email.com"
                    className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm outline-none focus:border-[#D4A017] transition-colors"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">CDL Class *</label>
                  <select
                    name="cdl_class"
                    required
                    className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm outline-none focus:border-[#D4A017] transition-colors bg-white cursor-pointer"
                  >
                    <option value="">Select CDL Class</option>
                    <option value="Class A">Class A</option>
                    <option value="Class B">Class B</option>
                    <option value="Class C">Class C</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">Years of Experience *</label>
                  <select
                    name="experience"
                    required
                    className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm outline-none focus:border-[#D4A017] transition-colors bg-white cursor-pointer"
                  >
                    <option value="">Select Experience</option>
                    <option value="Less than 1 year">Less than 1 year</option>
                    <option value="1-2 years">1–2 years</option>
                    <option value="3-5 years">3–5 years</option>
                    <option value="5+ years">5+ years</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">Current Location (City, State) *</label>
                <input
                  type="text"
                  name="location"
                  required
                  placeholder="Chicago, IL"
                  className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm outline-none focus:border-[#D4A017] transition-colors"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">Tell Us About Yourself</label>
                <textarea
                  name="message"
                  rows={4}
                  maxLength={500}
                  placeholder="Brief background, availability, type of routes you prefer..."
                  className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm outline-none focus:border-[#D4A017] transition-colors resize-none"
                ></textarea>
                <p className="text-xs text-gray-400 mt-1">Max 500 characters</p>
              </div>

              {applyStatus === 'success' && (
                <div className="bg-green-50 border border-green-200 text-green-700 rounded-xl px-4 py-3 text-sm flex items-center gap-2">
                  <i className="ri-checkbox-circle-line text-lg"></i>
                  Application submitted! We'll reach out to you soon.
                </div>
              )}
              {applyStatus === 'error' && (
                <div className="bg-red-50 border border-red-200 text-red-600 rounded-xl px-4 py-3 text-sm flex items-center gap-2">
                  <i className="ri-error-warning-line text-lg"></i>
                  Something went wrong. Please try again.
                </div>
              )}

              <button
                type="submit"
                className="w-full bg-[#D4A017] text-white py-4 rounded-xl font-bold text-base hover:bg-[#b8880f] transition-colors whitespace-nowrap cursor-pointer"
              >
                Submit Application
              </button>
            </form>
          </div>
        </div>
      </section>

      {/* CONTACT */}
      <section id="contact" className="py-24 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
            {/* Info */}
            <div>
              <span className="text-[#D4A017] text-sm font-bold uppercase tracking-widest">Contact</span>
              <h2 className="text-4xl md:text-5xl font-extrabold text-gray-900 mt-3 mb-6 leading-tight">
                Let's Talk<br />Business
              </h2>
              <p className="text-gray-500 text-base leading-relaxed mb-10">
                Have a load that needs moving? Want to discuss rates or availability? Reach out — we respond fast.
              </p>

              <div className="space-y-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 flex items-center justify-center bg-[#D4A017]/10 rounded-xl flex-shrink-0">
                    <i className="ri-map-pin-line text-[#D4A017] text-xl"></i>
                  </div>
                  <div>
                    <div className="text-xs text-gray-400 font-semibold uppercase tracking-wider">Location</div>
                    <div className="text-gray-800 font-semibold">Chicago, Illinois, USA</div>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 flex items-center justify-center bg-[#D4A017]/10 rounded-xl flex-shrink-0">
                    <i className="ri-phone-line text-[#D4A017] text-xl"></i>
                  </div>
                  <div>
                    <div className="text-xs text-gray-400 font-semibold uppercase tracking-wider">Phone</div>
                    <a href="tel:+16302024180" className="text-gray-800 font-semibold hover:text-[#D4A017] transition-colors">+1 (630) 202-4180</a>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 flex items-center justify-center bg-[#D4A017]/10 rounded-xl flex-shrink-0">
                    <i className="ri-mail-line text-[#D4A017] text-xl"></i>
                  </div>
                  <div>
                    <div className="text-xs text-gray-400 font-semibold uppercase tracking-wider">Email</div>
                    <a href="mailto:rulerlogistics@gmail.com" className="text-gray-800 font-semibold hover:text-[#D4A017] transition-colors">rulerlogistics@gmail.com</a>
                  </div>
                </div>
              </div>

              {/* Map */}
              <div className="mt-10 rounded-2xl overflow-hidden h-56 shadow-md">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d190258.6!2d-87.9073!3d41.8336!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x880e2c3cd0f4cbed%3A0xafe0a6ad09c0c000!2sChicago%2C%20IL!5e0!3m2!1sen!2sus!4v1234567890"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Ruler Logistics Chicago Location"
                ></iframe>
              </div>
            </div>

            {/* Form */}
            <div className="bg-gray-50 rounded-3xl p-8 border border-gray-100 shadow-sm">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Send a Message</h3>
              <form data-readdy-form id="contact-form" onSubmit={handleContact} className="space-y-5">
                <div>
                  <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">Your Name *</label>
                  <input
                    type="text"
                    name="name"
                    required
                    placeholder="Full name"
                    className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm outline-none focus:border-[#D4A017] transition-colors bg-white"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">Email *</label>
                  <input
                    type="email"
                    name="email"
                    required
                    placeholder="your@email.com"
                    className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm outline-none focus:border-[#D4A017] transition-colors bg-white"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">Phone</label>
                  <input
                    type="tel"
                    name="phone"
                    placeholder="+1 (312) 000-0000"
                    className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm outline-none focus:border-[#D4A017] transition-colors bg-white"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">Message *</label>
                  <textarea
                    name="message"
                    required
                    rows={5}
                    maxLength={500}
                    placeholder="Tell us about your freight needs..."
                    className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm outline-none focus:border-[#D4A017] transition-colors bg-white resize-none"
                  ></textarea>
                  <p className="text-xs text-gray-400 mt-1">Max 500 characters</p>
                </div>

                {contactStatus === 'success' && (
                  <div className="bg-green-50 border border-green-200 text-green-700 rounded-xl px-4 py-3 text-sm flex items-center gap-2">
                    <i className="ri-checkbox-circle-line text-lg"></i>
                    Message sent! We'll get back to you shortly.
                  </div>
                )}
                {contactStatus === 'error' && (
                  <div className="bg-red-50 border border-red-200 text-red-600 rounded-xl px-4 py-3 text-sm flex items-center gap-2">
                    <i className="ri-error-warning-line text-lg"></i>
                    Something went wrong. Please try again.
                  </div>
                )}

                <button
                  type="submit"
                  className="w-full bg-[#1a1a2e] text-white py-4 rounded-xl font-bold text-base hover:bg-[#2a2a4e] transition-colors whitespace-nowrap cursor-pointer"
                >
                  Send Message
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="bg-[#1a1a2e] text-white py-12">
        <div className="max-w-6xl mx-auto px-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-8 mb-8">
            <img
              src="https://public.readdy.ai/ai/img_res/9444abc7-4567-436f-8d58-59ae5de20165.png"
              alt="Ruler Logistics LLC"
              className="h-48 w-auto object-contain"
            />
            <div className="flex flex-wrap gap-6 justify-center">
              {navLinks.map(link => (
                <button
                  key={link.id}
                  onClick={() => scrollTo(link.id)}
                  className="text-white/60 hover:text-[#D4A017] text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
                >
                  {link.label}
                </button>
              ))}
            </div>
            <div className="flex gap-4">
              <a href="https://www.facebook.com/share/1CKi4DiqtR/?mibextid=wwXIfr" target="_blank" rel="nofollow" className="w-9 h-9 flex items-center justify-center rounded-full bg-white/10 hover:bg-[#D4A017] transition-colors cursor-pointer">
                <i className="ri-facebook-fill text-sm"></i>
              </a>
              <a href="https://www.instagram.com/rulerlogistics?igsh=aDNmcjl0c3lxZ3Jh" target="_blank" rel="nofollow" className="w-9 h-9 flex items-center justify-center rounded-full bg-white/10 hover:bg-[#D4A017] transition-colors cursor-pointer">
                <i className="ri-instagram-line text-sm"></i>
              </a>
              <a href="#" rel="nofollow" className="w-9 h-9 flex items-center justify-center rounded-full bg-white/10 hover:bg-[#D4A017] transition-colors cursor-pointer">
                <i className="ri-linkedin-fill text-sm"></i>
              </a>
            </div>
          </div>
          <div className="border-t border-white/10 pt-6 text-center text-white/40 text-xs">
            © 2025 Ruler Logistics LLC — Chicago, Illinois. All rights reserved.
          </div>
        </div>
      </footer>

    </div>
  );
}
